import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { Edit, Trash2, Plus, Eye, EyeOff, Search, X } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table';

import { CategoryModal } from './CategoryModal';
import { toast } from 'sonner';
import UserContext from '../context/UserContext';
import categoryService from '../services/categoryService.js';

// Import component-specific CSS (SOLID Principle)
import './CategoriesTable.css';

interface Category {
  id: number;
  name: string;
  name_ar?: string;
  description?: string;
  is_active: boolean;
  status: string;
  parent_id?: number | null;
  subcategories?: Category[];
  parent?: Category;
  fullPath?: Array<{ id: number; name: string; name_ar?: string }>;
  productCount?: number;
  createdAt?: string;
  updatedAt?: string;
}

export function CategoriesTable() {
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState<Category | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredCategories, setFilteredCategories] = useState<Category[]>([]);

  // Permissions from context (clean single definition)
  const { permissions } = useContext(UserContext);
  const permissionArray = Array.isArray(permissions) ? (permissions as string[]) : [];
  const has = (perm: string): boolean => permissionArray.includes(perm);

  const canManageCategories = has('manage_categories');
  const canViewCategories = has('view_categories') || canManageCategories;
  const canCreateCategories = canManageCategories || has('create_categories');
  const canEditCategories = canManageCategories || has('edit_category');
  const canDeleteCategories = canManageCategories || has('delete_category');

  const hasAnyActions = canEditCategories || canDeleteCategories || canManageCategories;
  const navigate = useNavigate();

  // Load categories on component mount and listen for changes
  useEffect(() => {
    if (canViewCategories) {
      loadCategories();
    } else {
      console.log('Cannot view categories - permission denied');
      setLoading(false);
    }

    // Listen for category changes
    const handleCategoriesChanged = (event: any) => {
      console.log('Categories changed event received:', event.detail);
      // Immediately refresh categories when changes are detected
      loadCategories(true); // Bypass cache for instant update
    };

    window.addEventListener('categoriesChanged', handleCategoriesChanged);

    return () => {
      window.removeEventListener('categoriesChanged', handleCategoriesChanged);
    };
  }, [canViewCategories]);

  // Filter categories based on search term
  useEffect(() => {
    const filtered = categories.filter(category =>
      category &&
      ((category.name && category.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (category.name_ar && category.name_ar.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (category.description && category.description.toLowerCase().includes(searchTerm.toLowerCase())))
    );
    setFilteredCategories(filtered);
  }, [categories, searchTerm]);

  const loadCategories = async (bypassCache = false) => {
    try {
      console.log('Loading categories...', bypassCache ? '(bypassing cache)' : '');
      setLoading(true);
      setError(null);
      const response = await categoryService.getCategories({}, bypassCache);
      console.log('Categories response:', response);
      const categoriesData = response?.data || response || [];
      setCategories(categoriesData);
      console.log('Categories loaded:', categoriesData?.length || 0);
    } catch (err) {
      console.error('Error loading categories:', err);
      setError('Failed to load categories');
      toast.error('Failed to load categories');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditingCategory(null);
    setIsModalOpen(true);
  };

  const handleEdit = (category: Category) => {
    setEditingCategory(category);
    setIsModalOpen(true);
  };

  const handleDelete = async (id: number) => {
    if (!confirm('Are you sure you want to delete this category?')) return;

    try {
      await categoryService.deleteCategory(id);
      setCategories(categories.filter(category => category.id !== id));
      toast.success('Category deleted successfully');
    } catch (error: any) {
      console.error('Error deleting category:', error);
      toast.error(error.response?.data?.error?.message || 'Failed to delete category');
    }
  };

  const handleToggleStatus = async (id: number) => {
    const category = categories.find(c => c.id === id);
    if (!category) return;

    try {
      // Optimistically update UI first
      const newStatus = !category.is_active;
      setCategories(categories.map(c =>
        c.id === id ? { ...c, is_active: newStatus } : c
      ));

      await categoryService.toggleCategoryStatus(id);

      // Reload fresh data from server to ensure consistency (bypass cache)
      await loadCategories(true);

      toast.success(`Category ${newStatus ? 'activated' : 'deactivated'} successfully`);
    } catch (error: any) {
      console.error('Error toggling category status:', error);

      // Revert optimistic update on error
      setCategories(categories.map(c =>
        c.id === id ? { ...c, is_active: category.is_active } : c
      ));

      toast.error(error.response?.data?.error?.message || 'Failed to toggle category status');
    }
  };

  const handleSave = async (categoryData: Omit<Category, 'id'>) => {
    try {
      if (editingCategory) {
        const updatedCategory = await categoryService.updateCategory(editingCategory.id, categoryData);
        setCategories(categories.map(category =>
          category.id === editingCategory.id ? updatedCategory : category
        ));
        toast.success('Category updated successfully');
      } else {
        const newCategory = await categoryService.createCategory(categoryData);
        setCategories([...categories, newCategory]);
        toast.success('Category created successfully');
      }
      setIsModalOpen(false);
    } catch (error: any) {
      console.error('Error saving category:', error);
      toast.error(error.response?.data?.error?.message || 'Failed to save category');
    }
  };

  // Filter categories by status for separate tables
  const registeredCategories = filteredCategories.filter(category => category.status === 'Registered');
  const underRegistrationCategories = filteredCategories.filter(category => category.status === 'Under Registration');

  return (
    <>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Categories Management</CardTitle>
          {canCreateCategories && (
            <Button
              onClick={handleAdd}
              className="bg-[#3B82F6] hover:bg-[#2563EB]"
              style={{ background: 'linear-gradient(90deg, #3fa46eff, #246844ff)'}}
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Category
            </Button>
          )}
        </CardHeader>
        <CardContent>
          {/* Search Controls */}
          <div className="mb-6">
            <div className="flex flex-col sm:flex-row gap-4 items-center">
              {/* Search Input */}
                            <div className="flex-1">
                              <div className="flex items-center gap-2 px-3 py-2 bg-muted/60 border border-border rounded-full shadow-sm focus-within:ring-2 focus-within:ring-primary/40 focus-within:border-primary/60 transition-all">
                                <Search className="w-4 h-4 text-muted-foreground" />
                                <Input
                                  placeholder="Search products..."
                                  value={searchTerm}
                                  onChange={(e) => setSearchTerm(e.target.value)}
                                  className="border-0 bg-transparent shadow-none focus-visible:ring-0 focus-visible:ring-offset-0 px-0 text-sm text-left"
                                />
                              </div>
                            </div>

              {/* Clear Search Button */}
              {searchTerm && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setSearchTerm('')}
                  className="text-xs"
                >
                  <X className="w-3 h-3 mr-1" />
                  Clear
                </Button>
              )}
            </div>

            {/* Results Summary */}
            {searchTerm && (
              <div className="text-sm text-muted-foreground mt-2">
                {loading ? (
                  'Searching...'
                ) : (
                  `Showing ${filteredCategories.length} of ${categories.length} categories matching "${searchTerm}"`
                )}
              </div>
            )}
          </div>

          {loading ? (
            <div className="text-center py-8">Loading categories...</div>
          ) : error ? (
            <div className="text-center py-8 text-destructive">{error}</div>
          ) : (
            <>
              {/* Registered Categories Table */}
              {registeredCategories.length > 0 && (
                <div className="mb-8">
                  <h3 className="text-lg font-semibold mb-4">Registered Categories</h3>
                  <div className="rounded-lg border border-border overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Name</TableHead>
                          <TableHead>Description</TableHead>
                          <TableHead>Products</TableHead>
                          {hasAnyActions && <TableHead className="text-right">Actions</TableHead>}
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {registeredCategories.map((category) => (
                          <TableRow key={`registered-${category.id}`}
                          className="cursor-pointer hover:bg-muted/50"
                          onClick={
                            () => navigate('/home#products', { state: { selectedCategory: category.id.toString() } })
                            }>
                            <TableCell className="text-left">
                              <div>
                                <div className="font-medium">{category.name}</div>
                                {category.name_ar && (
                                  <div className="text-sm text-muted-foreground">{category.name_ar}</div>
                                )}
                              </div>
                            </TableCell>
                            <TableCell className="text-left">
                              <div className="truncate max-w-xs">{category.description || '-'}</div>
                            </TableCell>
                            <TableCell className="text-left">
                              {category.productCount || 0}
                            </TableCell>
                            {hasAnyActions && (
                              <TableCell className="text-right">
                                <div className="flex justify-end gap-2">
                                  {/* Edit allowed with manage_categories or edit_category */}
                                  {canEditCategories && (
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleEdit(category);
                                      }}
                                    >
                                      <Edit className="w-4 h-4" />
                                    </Button>
                                  )}
                                  {/* Delete allowed with manage_categories or delete_category */}
                                  {canDeleteCategories && (
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleDelete(category.id);
                                      }}
                                      className="text-destructive hover:text-destructive"
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </Button>
                                  )}
                                </div>
                              </TableCell>
                            )}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              )}

              {/* Under Registration Categories Table */}
              {underRegistrationCategories.length > 0 && (
                <div className="mb-8">
                  <h3 className="text-lg font-semibold mb-4">Under Registration Categories</h3>
                  <div className="rounded-lg border border-border overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Name</TableHead>
                          <TableHead>Description</TableHead>
                          <TableHead>Products</TableHead>
                          {hasAnyActions && <TableHead className="text-right">Actions</TableHead>}
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {underRegistrationCategories.map((category) => (
                          <TableRow key={`registered-${category.id}`}
                          onClick={
                            () => navigate('/home#products', { state: { selectedCategory: category.id.toString() } })
                            }>
                            <TableCell className="text-left">
                              <div>
                                <div className="font-medium">{category.name}</div>
                                {category.name_ar && (
                                  <div className="text-sm text-muted-foreground">{category.name_ar}</div>
                                )}
                              </div>
                            </TableCell>
                            <TableCell className="text-left">
                              <div className="truncate max-w-xs">{category.description || '-'}</div>
                            </TableCell>
                            <TableCell className="text-left">
                              {category.productCount || 0}
                            </TableCell>
                            {hasAnyActions && (
                              <TableCell className="text-right">
                                <div className="flex justify-end gap-2">
                                  {/* Edit allowed with manage_categories or edit_category */}
                                  {canEditCategories && (
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleEdit(category);
                                      }}
                                    >
                                      <Edit className="w-4 h-4" />
                                    </Button>
                                  )}
                                  {/* Delete allowed with manage_categories or delete_category */}
                                  {canDeleteCategories && (
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleDelete(category.id);
                                      }}
                                      className="text-destructive hover:text-destructive"
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </Button>
                                  )}
                                </div>
                              </TableCell>
                            )}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              )}

              {!loading && filteredCategories.length === 0 && (
                <div className="text-center py-8 text-muted-foreground">
                  {searchTerm ? 'No categories match your search' : 'No categories found'}
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      <CategoryModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSave={handleSave}
        category={editingCategory}
      />
    </>
  );
}
